/**
 * Dynamic Columns Service
 * Integrates with backend custom-list module API
 * Manages dynamic column configurations per module/form with fallback
 *
 * Backend API: /web/custom-list, /web/list-config
 * Backend Module: backend/custom-list
 */

import apiService from './apiClient';
import logger from '../utils/logger';

// Column Types (aligned with backend field system types)
export enum DynamicColumnType {
  TEXT = 'TEXT',
  TEXTAREA = 'TEXTAREA',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  DATETIME = 'DATETIME',
  BOOLEAN = 'BOOLEAN',
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
  DROPDOWN = 'DROPDOWN',
  SELECT = 'SELECT',
  MULTI_SELECT = 'MULTI_SELECT',
  UPLOAD = 'UPLOAD',
  FILE = 'FILE',
  IMAGE = 'IMAGE',
  URL = 'URL',
}

// Column Configuration
export interface DynamicColumn {
  fieldKey: string;
  label: string;
  type: DynamicColumnType;
  width?: number;
  sortable?: boolean;
  order: number;
}

// Dynamic Column List (matches backend CustomListEntity)
export interface DynamicColumnList {
  id: string;
  organizationId: number;
  moduleId: string;
  formId: string;
  listName: string;
  listTitle: string;
  isDefault: boolean;
  columns: DynamicColumn[];
  isActive: boolean;
  createdBy: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

// DTOs
export interface CreateDynamicColumnListDto {
  moduleId: string;
  formId: string;
  listName: string;
  listTitle: string;
  isDefault?: boolean;
  columns: DynamicColumn[];
}

export interface UpdateDynamicColumnListDto {
  listTitle?: string;
  columns?: DynamicColumn[];
  isDefault?: boolean;
  isActive?: boolean;
}

// List config response with fallback info
export interface DynamicColumnListConfig {
  source: 'custom' | 'default';
  config: DynamicColumnList;
}

class DynamicColumnsService {
  private baseUrl = '/web/custom-list';
  private configUrl = '/web/list-config';

  /**
   * Get list configuration with fallback mechanism
   * Returns custom list if exists, otherwise falls back to default list
   * GET /web/list-config/:moduleId/:formId
   */
  async getListConfig(moduleId: string, formId: string): Promise<DynamicColumnListConfig> {
    try {
      logger.info('Fetching list config with fallback', { moduleId, formId });

      const response = await apiService.get(`${this.configUrl}/${moduleId}/${formId}`);

      return {
        source: response.data?.source || response.source || 'default',
        config: response.data?.config || response.config || response.data || response,
      };
    } catch (error) {
      logger.error('Failed to fetch list config', error);
      throw error;
    }
  }

  /**
   * Get all custom lists for module/form
   * GET /web/list-config/:moduleId/:formId/all
   */
  async getAllLists(moduleId: string, formId: string): Promise<DynamicColumnList[]> {
    try {
      logger.info('Fetching all lists for module', { moduleId, formId });

      const response = await apiService.get(
        `${this.configUrl}/${moduleId}/${formId}/all`
      );

      return response.data || response || [];
    } catch (error) {
      logger.error('Failed to fetch all lists', error);
      return [];
    }
  }

  /**
   * Save list configuration
   * POST /web/list-config/:moduleId/:formId
   */
  async saveListConfig(
    moduleId: string,
    formId: string,
    listData: Omit<CreateDynamicColumnListDto, 'moduleId' | 'formId'>
  ): Promise<DynamicColumnList> {
    try {
      logger.info('Saving list config', { moduleId, formId, listData });

      const response = await apiService.post(
        `${this.configUrl}/${moduleId}/${formId}`,
        listData
      );

      return response.data || response;
    } catch (error) {
      logger.error('Failed to save list config', error);
      throw error;
    }
  }

  /**
   * Update list configuration
   * PUT /web/list-config/:listId
   */
  async updateListConfig(
    listId: string,
    updateData: UpdateDynamicColumnListDto
  ): Promise<DynamicColumnList> {
    try {
      logger.info('Updating list config', { listId, updateData });

      const response = await apiService.put(`${this.configUrl}/${listId}`, updateData);

      return response.data || response;
    } catch (error) {
      logger.error('Failed to update list config', error);
      throw error;
    }
  }

  /**
   * Delete list configuration (soft delete)
   * DELETE /web/list-config/:listId
   */
  async deleteListConfig(listId: string): Promise<void> {
    try {
      logger.info('Deleting list config', { listId });

      await apiService.delete(`${this.configUrl}/${listId}`);
    } catch (error) {
      logger.error('Failed to delete list config', error);
      throw error;
    }
  }

  /**
   * Set list as default
   */
  async setAsDefault(listId: string): Promise<DynamicColumnList> {
    try {
      logger.info('Setting list as default', { listId });

      return await this.updateListConfig(listId, { isDefault: true });
    } catch (error) {
      logger.error('Failed to set list as default', error);
      throw error;
    }
  }

  /**
   * Create list (alternative method using base URL)
   * POST /web/custom-list
   */
  async createList(listData: CreateDynamicColumnListDto): Promise<DynamicColumnList> {
    try {
      logger.info('Creating list', { listData });

      const response = await apiService.post(this.baseUrl, listData);

      return response.data || response;
    } catch (error) {
      logger.error('Failed to create list', error);
      throw error;
    }
  }

  /**
   * Get list by ID
   * GET /web/custom-list/:id
   */
  async getListById(listId: string): Promise<DynamicColumnList> {
    try {
      logger.info('Fetching list by ID', { listId });

      const response = await apiService.get(`${this.baseUrl}/${listId}`);

      return response.data || response;
    } catch (error) {
      logger.error('Failed to fetch list', error);
      throw error;
    }
  }

  /**
   * Update list (alternative method using base URL)
   * PUT /web/custom-list/:id
   */
  async updateList(
    listId: string,
    updateData: UpdateDynamicColumnListDto
  ): Promise<DynamicColumnList> {
    try {
      logger.info('Updating list', { listId, updateData });

      const response = await apiService.put(`${this.baseUrl}/${listId}`, updateData);

      return response.data || response;
    } catch (error) {
      logger.error('Failed to update list', error);
      throw error;
    }
  }

  /**
   * Delete list (alternative method using base URL)
   * DELETE /web/custom-list/:id
   */
  async deleteList(listId: string): Promise<void> {
    try {
      logger.info('Deleting list', { listId });

      await apiService.delete(`${this.baseUrl}/${listId}`);
    } catch (error) {
      logger.error('Failed to delete list', error);
      throw error;
    }
  }
}

export const dynamicColumnsService = new DynamicColumnsService();
export default dynamicColumnsService;
